import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { SupportedLanguage } from "@pulse/i18n";
import { isSupportedLanguage } from "@pulse/i18n";
import type { Tables, TablesInsert, TablesUpdate } from "@/types/database";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";
import { MUSIC_GENRES, type MusicGenre } from "@/lib/musicGenres";

export const MUSIC_STYLE_TAGS = MUSIC_GENRES.map((g) => g.id);

export const CATEGORY_TAGS = [
  "Rooftop",
  "Afterwork",
  "Club",
  "Bar",
  "Concert",
] as const;

export type MusicStyleTag = MusicGenre;
export type CategoryTag = (typeof CATEGORY_TAGS)[number];

export const NIGHT_STYLE_TAGS = ["calm", "dance", "surprise"] as const;
export type NightStyleTag = (typeof NIGHT_STYLE_TAGS)[number];

export interface UserPreferences {
  musicStyles: string[];
  categories: string[];
  maxBudget: number;
  nightStyles: string[];
  dislikeNote: string | null;
  preferredLanguage: SupportedLanguage | null;
}

type PreferencesRow = Tables<"user_preferences">;

function mapRow(row: PreferencesRow): UserPreferences {
  const lang = row.preferred_language?.slice(0, 2);
  return {
    musicStyles: row.music_styles ?? [],
    categories: row.categories ?? [],
    maxBudget: Number(row.max_budget ?? 50),
    nightStyles: row.night_styles ?? [],
    dislikeNote: row.dislike_note ?? null,
    preferredLanguage: isSupportedLanguage(lang) ? lang : null,
  };
}

export function userPreferencesKey(userId: string | undefined) {
  return ["user-preferences", userId ?? "none"] as const;
}

export async function fetchUserPreferences(
  userId: string,
): Promise<UserPreferences | null> {
  const { data, error } = await supabase
    .from("user_preferences")
    .select(
      "id, user_id, music_styles, categories, max_budget, night_styles, dislike_note, preferred_language, updated_at",
    )
    .eq("user_id", userId)
    .maybeSingle();

  if (error) throw error;
  return data ? mapRow(data) : null;
}

/** Upsert only preferred_language (fire-and-forget safe from language switchers). */
export async function persistConsumerPreferredLanguage(
  userId: string,
  lang: SupportedLanguage,
): Promise<void> {
  const updatedAt = new Date().toISOString();
  const { data: existing } = await supabase
    .from("user_preferences")
    .select("id")
    .eq("user_id", userId)
    .maybeSingle();

  if (existing) {
    const { error } = await supabase
      .from("user_preferences")
      .update({ preferred_language: lang, updated_at: updatedAt })
      .eq("user_id", userId);
    if (error) throw error;
    return;
  }

  const { error } = await supabase.from("user_preferences").insert({
    user_id: userId,
    preferred_language: lang,
    music_styles: [],
    categories: [],
    night_styles: [],
    max_budget: 50,
    dislike_note: null,
    updated_at: updatedAt,
  });
  if (error) throw error;
}

export function useUserPreferences() {
  const { user, isGuest } = useAuth();
  const userId = user && !isGuest ? user.id : undefined;

  return useQuery({
    queryKey: userPreferencesKey(userId),
    queryFn: () => fetchUserPreferences(userId!),
    enabled: !!userId,
    staleTime: 60_000,
  });
}

export function useSaveUserPreferences() {
  const { user, isGuest } = useAuth();
  const userId = user && !isGuest ? user.id : undefined;
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (prefs: UserPreferences) => {
      if (!userId) throw new Error("Sign in to save preferences.");

      const payload: TablesInsert<"user_preferences"> = {
        user_id: userId,
        music_styles: prefs.musicStyles,
        categories: prefs.categories,
        max_budget: prefs.maxBudget,
        night_styles: prefs.nightStyles,
        dislike_note: prefs.dislikeNote?.trim() ? prefs.dislikeNote.trim() : null,
        preferred_language: prefs.preferredLanguage,
        updated_at: new Date().toISOString(),
      };

      const { data: existing } = await supabase
        .from("user_preferences")
        .select("id")
        .eq("user_id", userId)
        .maybeSingle();

      if (existing) {
        const update: TablesUpdate<"user_preferences"> = {
          music_styles: prefs.musicStyles,
          categories: prefs.categories,
          max_budget: prefs.maxBudget,
          night_styles: prefs.nightStyles,
          dislike_note: prefs.dislikeNote?.trim() ? prefs.dislikeNote.trim() : null,
          preferred_language: prefs.preferredLanguage,
          updated_at: new Date().toISOString(),
        };
        const { error } = await supabase
          .from("user_preferences")
          .update(update)
          .eq("user_id", userId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("user_preferences").insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userPreferencesKey(userId) });
      queryClient.invalidateQueries({ queryKey: ["recommendations"] });
    },
  });

  return {
    canSave: !!userId,
    isSaving: mutation.isPending,
    saveError: mutation.error,
    save: mutation.mutateAsync,
  };
}

export function toggleTag(list: string[], tag: string): string[] {
  return list.includes(tag) ? list.filter((t) => t !== tag) : [...list, tag];
}

export function interestTagLabel(tag: string): string {
  const genre = MUSIC_GENRES.find((g) => g.id === tag);
  if (genre) return genre.label;
  return tag;
}
